import {
	createContext,
	use,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";

import { api } from "./dajia-client";
import {
	getSessionKey,
	setSessionKey,
	getWorkspaceKey,
	setWorkspaceKey,
} from "./dajia-session-key";
export type UserSession = {
	authenticated: true;
	session_key: string | null;
	home_workspace: string;
	read_only: boolean;
	workspaces: { id: string; name: string }[];
	user: {
		id: string;
		username: string;
		name: string;
		role: "admin" | "member";
	};
	workspace: { id: string; name: string; description: string };
};
type Session = UserSession | { authenticated: false };
type Context = {
	session: Session | null;
	error: string;
	refresh: () => Promise<void>;
	signIn: (username: string, password: string) => Promise<void>;
	signOut: () => Promise<void>;
	switchAccount: () => void;
	switchWorkspace: (id: string) => Promise<boolean>;
};
const SessionContext = createContext<Context | null>(null);
function broadcast(key: string | null) {
	localStorage.setItem(
		"dajia-account-change",
		JSON.stringify({ key, nonce: `${Date.now()}-${Math.random()}` }),
	);
}
export function SessionProvider({ children }: { children: ReactNode }) {
	const [session, setSession] = useState<Session | null>(null),
		[error, setError] = useState("");
	const generation = useRef(0);
	const authenticating = useRef(false);
	const refresh = useCallback(async () => {
		if (authenticating.current) {
			return;
		}
		const version = ++generation.current;
		try {
			const next = await api<Session>("session");
			if (version === generation.current) {
				if (next.authenticated) {
					setSessionKey(next.session_key);
				}
				setSession(next);
				setError("");
			}
		} catch {
			if (version === generation.current) {
				setError("暂时无法连接服务，请重试。");
			}
		}
	}, []);
	useEffect(() => {
		if (window.location.pathname === "/login") {
			setSessionKey("none");
			setWorkspaceKey(null);
		}
		void Promise.resolve().then(refresh);
		const timer = setInterval(() => void refresh(), 60000);
		const expired = () => {
			generation.current++;
			setSession({ authenticated: false });
			void refresh();
		};
		const changed = (event: StorageEvent) => {
			if (event.key === "dajia-account-change" && event.newValue) {
				try {
					if (JSON.parse(event.newValue).key === getSessionKey()) {
						expired();
					}
				} catch {
					/* Ignore unrelated storage values. */
				}
			}
		};
		const focus = () => void refresh();
		window.addEventListener("dajia-session-expired", expired);
		window.addEventListener("storage", changed);
		window.addEventListener("focus", focus);
		return () => {
			clearInterval(timer);
			window.removeEventListener("dajia-session-expired", expired);
			window.removeEventListener("storage", changed);
			window.removeEventListener("focus", focus);
		};
	}, [refresh]);
	const signIn = useCallback(
		async (username: string, password: string) => {
			authenticating.current = true;
			const version = ++generation.current;
			try {
				setWorkspaceKey(null);
				const next = await api<UserSession>("login", "", {
					username,
					password,
				});
				if (version === generation.current) {
					setSessionKey(next.session_key);
					setSession(next);
					setError("");
				}
			} finally {
				authenticating.current = false;
				if (version !== generation.current) {
					await refresh();
				}
			}
		},
		[refresh],
	);
	const signOut = useCallback(async () => {
		authenticating.current = true;
		generation.current++;
		try {
			const oldKey = getSessionKey();
			await api("logout", "", {});
			setSessionKey("none");
			setWorkspaceKey(null);
			broadcast(oldKey);
			setSession({ authenticated: false });
			setError("");
		} finally {
			authenticating.current = false;
		}
	}, []);
	const switchAccount = useCallback(() => {
		generation.current++;
		setSessionKey("none");
		setWorkspaceKey(null);
		setSession({ authenticated: false });
		setError("");
	}, []);
	const switchWorkspace = useCallback(
		async (id: string) => {
			if (
				!session?.authenticated ||
				!session.workspaces.some((w) => w.id === id)
			) {
				return false;
			}
			const previous = session,
				oldKey = getWorkspaceKey(),
				version = ++generation.current;
			authenticating.current = true;
			setSession(null);
			setWorkspaceKey(id);
			try {
				const next = await api<UserSession>("session");
				if (version === generation.current) {
					setSession(next);
					setError("");
				}
				return true;
			} catch {
				if (version === generation.current) {
					setWorkspaceKey(oldKey);
					setSession(previous);
					setError("单位切换未完成，请重试。");
				}
				return false;
			} finally {
				authenticating.current = false;
			}
		},
		[session],
	);
	const context = useMemo(
		() => ({
			session,
			error,
			refresh,
			signIn,
			signOut,
			switchAccount,
			switchWorkspace,
		}),
		[session, error, refresh, signIn, signOut, switchAccount, switchWorkspace],
	);

	return <SessionContext value={context}>{children}</SessionContext>;
}
export function useSession() {
	const value = use(SessionContext);
	if (!value) {
		throw new Error("SessionProvider required");
	}
	return value;
}
export function useWorkspace() {
	const { session } = useSession();
	if (!session?.authenticated) {
		throw new Error("Login required");
	}
	return session;
}
